import { Alert, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useGetDriverTermsDataQuery } from "@/libs/generated/hooks";
import type { GetDriverTermsDataQueryVariables } from "@/libs/generated/operations";
import type { DriverObject } from "@/libs/generated/types";
import { RoutePaths } from "@/utils/constants/routes";
import { DriverExpiryPauseBadge } from "../DriverExpiryPauseBadge";
import DriverForm from "./driver-form-simple";
import DriverTermsState from "./driver-terms-state";
import DriverToolBar from "./toolbar";
import type { TDriverData } from "./types";

type TDriverRequestDetails = {
  showTerms?: boolean;
  driverData: TDriverData;
  actions?: React.ReactNode;
};

function DriverRequestDetails({ showTerms, driverData, actions }: TDriverRequestDetails) {
  const { id } = useParams();
  const { t } = useTranslation();
  const location = useLocation();

  if (!id) throw new Error("no driver id in params");

  const isWaitingRequest = location.pathname.includes(RoutePaths.waitingRequests);

  // const uniqueId = useId();

  const {
    data: terms,
    error: termsError,
    isLoading: termsLoading,
  } = useGetDriverTermsDataQuery({ driverId: Number(id) } as GetDriverTermsDataQueryVariables, {
    enabled: !isWaitingRequest,
  });

  if (termsLoading) return <CircularProgress />;

  if (termsError && !isWaitingRequest) {
    // return <p>{t("common.error.generic")}</p>;
  }
  // Only validate terms data if not a waiting request
  if (!isWaitingRequest && !terms?.getDriverTermsData) {
    return <p>No driver terms data found</p>;
  }

  const showToolBar =
    driverData.status === "ACCEPTED" ||
    driverData.status === "DISABLED" ||
    (!driverData.status && driverData.confirmedByAlb);

  return (
    <Container sx={{ py: 4 }} maxWidth="xl">
      <Grid container spacing={2}>
        {showToolBar && (
          <Grid size={{ xs: 12 }}>
            <DriverToolBar driverData={driverData as DriverObject} />
          </Grid>
        )}

        {(driverData.rejectedByAlb || driverData.status === "REJECTED") &&
          (driverData.rejectionReason || driverData.rejectionAdditionalInfo) && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">
                <Typography variant="subtitle2">{t("driver.rejectReason.label")}</Typography>
                {driverData.rejectionReason && (
                  <Typography variant="body2">
                    {t(`driver.rejectReason.${driverData.rejectionReason}`, {
                      defaultValue: driverData.rejectionReason,
                    })}
                  </Typography>
                )}
                {driverData.rejectionAdditionalInfo && (
                  <Typography variant="body2" color="text.secondary">
                    {driverData.rejectionAdditionalInfo}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}

        {driverData.isDocumentExpiryPaused && (
          <Grid size={{ xs: 12 }}>
            <DriverExpiryPauseBadge
              size="medium"
              pausedAt={driverData.documentExpiryPausedAt}
              expiredDocuments={driverData.expiredDocuments}
            />
          </Grid>
        )}

        <DriverForm data={driverData} />

        {showTerms && (
          <Grid container size={{ xs: 12 }} spacing={2}>
            <DriverTermsState termsData={terms?.getDriverTermsData} driverData={driverData} />
          </Grid>
        )}

        {actions && actions}

        {/* <Grid xs={12}>
          <Questions data={driverData.mcqAnswers} />
        </Grid> */}
      </Grid>
    </Container>
  );
}

export default DriverRequestDetails;
